import { useRef } from 'react';

import { DefaultFormFieldLayout } from '@bambi/component-library/src/components/forms/DefaultFormFieldLayout/DefaultFormFieldLayout';
import { FormFieldContainer } from '@bambi/component-library/src/components/forms/layout/FormFieldContainer';
import { isSelectOption } from '@bambi/component-library/src/components/inputs/Select/SelectOption/isSelectOption';
import { isArray, uniqBy } from 'lodash-es';

import {
  FormFieldController,
  FormFieldControllerRulesProps,
} from 'src/common/FormField/FormFieldController';
import { MultiSelect } from 'src/common/primitives/MultiSelect/MultiSelect';

import type { HelperText } from '@bambi/component-library/src/components/forms/FormFieldHelperText/FormFieldHelperText';
import type {
  SelectOption,
  SelectOptionWithColor,
} from '@bambi/component-library/src/components/inputs/Select/SelectOption/SelectOption';

export interface MultiSelectContextFieldProps {
  fieldPath: string;
  label: string;
  placeholder?: string;
  options: SelectOption[] | SelectOptionWithColor[];
  disabled?: boolean;
  searchable?: boolean;
  query?: string;
  setQuery?: (query: string) => void;
  rules?: FormFieldControllerRulesProps;
  inline?: boolean;
  helperText?: HelperText;
  allowNew?: boolean;
  onSelectAll?: () => void;
}

export function MultiSelectContextField({
  fieldPath,
  label,
  placeholder,
  options,
  disabled,
  searchable,
  query,
  setQuery,
  rules,
  inline,
  helperText,
  allowNew,
  onSelectAll,
}: MultiSelectContextFieldProps) {
  const selectedRef = useRef<SelectOption[] | SelectOptionWithColor[]>([]);
  return (
    <FormFieldController name={fieldPath} rules={rules}>
      {({ field, fieldState }) => {
        const inputProps = {
          id: fieldPath,
          ...field,
        };
        // In the case where the user has selected some options and then
        // types in the search box, options might not have all the selected
        // options, so we keep the resolved selected options and merge them
        // Keeping this outside of MultiSelect since it's an uncontrolled component
        selectedRef.current =
          isArray(field.value) &&
          field.value.length > 0 &&
          field.value.some(isSelectOption)
            ? field.value
            : uniqBy([...options, ...selectedRef.current], 'value').filter(
                (option) =>
                  isArray(field.value) && field.value.includes(option.value)
              );
        return (
          <FormFieldContainer>
            <DefaultFormFieldLayout
              label={label}
              error={fieldState.error?.message?.toString()}
              inputId={inputProps.id}
              inline={inline}
              helperText={helperText}
            >
              <div className="flex items-center gap-2">
                <MultiSelect
                  disabled={disabled}
                  placeholder={placeholder}
                  options={options}
                  selected={selectedRef.current}
                  onChange={(selected) => {
                    const newValue =
                      isArray(field.value) &&
                      field.value.length > 0 &&
                      field.value.some(isSelectOption)
                        ? selected
                        : selected.map((option) => option.value);
                    field.onChange(newValue);
                  }}
                  searchable={searchable}
                  query={query}
                  setQuery={setQuery}
                  allowNew={allowNew}
                  onSelectAll={onSelectAll}
                />
              </div>
            </DefaultFormFieldLayout>
          </FormFieldContainer>
        );
      }}
    </FormFieldController>
  );
}
